import { rulesetVisualizer } from '../utils/rulesetVisualizer.js';
import { formatHexCode } from '../utils/utils.js';
import { classifyRulesetConstraint, CONSTRAINT_CLASS_META } from '../core/rulesetDescriptor.js';

/**
 * Short, human-friendly label for a paired initial condition, shown as the "IC" badge on a library
 * card. Falls back to the raw mode name for forward-compat IC types this build doesn't label yet.
 * @param {{mode: string, params?: object}} initialState
 * @returns {string}
 */
export function icBadgeLabel(initialState) {
    const mode = initialState?.mode;
    if (mode === 'clusters') return 'IC · clumps';
    if (mode === 'density') {
        const d = initialState?.params?.density;
        if (Number.isFinite(d)) return `IC · ${Math.round(d * 100)}% fill`;
        return 'IC · random fill';
    }
    return mode ? `IC · ${mode}` : 'IC';
}

/**
 * Badge descriptor for a ruleset's structural constraint class (roadmap #38) — the strictest of
 * `totalistic ⊂ n_count ⊂ d_sym ⊂ r_sym ⊂ free` its rule table satisfies. Derived from the hex on every
 * render, never stored: it is a fact about the table, not metadata someone can get wrong.
 * @param {string} hex 32-char ruleset hex.
 * @returns {{cls: string, label: string, title: string}|null} null for an unparseable hex.
 */
export function constraintBadge(hex) {
    const cls = classifyRulesetConstraint(hex);
    if (!cls) return null;
    const meta = CONSTRAINT_CLASS_META[cls];
    return { cls, label: meta.label, title: `${meta.label} ruleset — ${meta.description}` };
}

/**
 * A committed catalog entry may carry an optional `authorUrl` alongside its `author` credit (#27).
 * Only `http(s)` survives: the field is data, and a card must never turn data into a `javascript:`
 * link. Returns '' when there is nothing safe to link to.
 * @param {unknown} value
 * @returns {string}
 */
export function safeCreditUrl(value) {
    if (typeof value !== 'string' || !value.trim()) return '';
    try {
        const url = new URL(value.trim());
        return url.protocol === 'https:' || url.protocol === 'http:' ? url.toString() : '';
    } catch {
        return '';
    }
}

export class RulesetDisplayFactory {
    constructor(appContext) {
        this.appContext = appContext;
        this.observer = null;
        this.observedElements = new WeakMap();
        this._initObserver();
    }

    _initObserver() {
        const options = {
            root: null, 
            rootMargin: '100px', 
            threshold: 0.01
        };

        this.observer = new IntersectionObserver((entries, observer) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    const element = entry.target;
                    const vizPlaceholder = element.querySelector('.viz-placeholder');
                    if (vizPlaceholder) {
                        const { hex } = this.observedElements.get(element);
                        const colorSettings = this.appContext.colorController.getSettings();
                        const symmetryData = this.appContext.worldManager.getSymmetryData();
                        const svg = rulesetVisualizer.createRulesetSVG(hex, { width: '100%', height: '100%' }, colorSettings, symmetryData);
                        svg.classList.add('ruleset-viz-svg');
                        vizPlaceholder.replaceWith(svg);
                        observer.unobserve(element); 
                        this.observedElements.delete(element);
                    }
                }
            });
        }, options);
    }

    /**
     * Creates a card for the Ruleset Library (Public or Personal). The hero is the evolved-world
     * thumbnail when the entry has one (schema v2); otherwise it falls back to the lazily-rendered
     * rule-viz glyph (the IntersectionObserver path). When the entry carries a paired initial
     * condition it shows an IC badge; the list-level Paired start setting controls how Load behaves.
     * Every card also carries a visible Public/Mine source marker for the unified library list.
     * @param {object} ruleData - The full ruleset object (may include `tags`, `initialState`, `thumb`).
     * @param {boolean} isPersonal - True if it's from the user's library.
     * @returns {HTMLElement} The created card element.
     */
    createLibraryListItem(ruleData, isPersonal = false) {
        const item = document.createElement('div');
        item.className = 'library-card' + (isPersonal ? ' personal' : '');
        item.dataset.hex = ruleData.hex;
        item.dataset.source = isPersonal ? 'personal' : 'public';
        if (ruleData.id) item.dataset.id = ruleData.id;

        const hasIC = !!(ruleData.initialState && ruleData.initialState.mode);
        const tags = Array.isArray(ruleData.tags) ? ruleData.tags : [];

        const hero = ruleData.thumb
            ? `<img class="library-card-thumb-img" src="${this._escapeAttr(ruleData.thumb)}" alt="" loading="lazy" />`
            : `<div class="viz-placeholder"></div>`;
        const icBadge = hasIC
            ? `<span class="library-card-ic-badge" title="Paired initial condition">${this._escape(icBadgeLabel(ruleData.initialState))}</span>`
            : '';
        const tagChips = tags.length
            ? `<div class="library-card-tags">${tags.map(t => `<span class="tag-chip">${this._escape(t)}</span>`).join('')}</div>`
            : '';
        // Structural class sits in the title row, not among the tag chips: tags are claims about
        // what a rule *does* (and are editable), this is a derived fact about the table itself.
        const badge = constraintBadge(ruleData.hex);
        const constraintChip = badge
            ? `<span class="constraint-badge constraint-${badge.cls}" title="${this._escapeAttr(badge.title)}">${this._escape(badge.label)}</span>`
            : '';
        const sourceBadge = isPersonal
            ? '<span class="library-card-source source-personal" title="Saved in your personal library">Mine</span>'
            : '<span class="library-card-source source-public" title="Included in the public library">Public</span>';

        // Community credit (#27): committed catalog entries can name whoever submitted them. Assigned
        // at review from the GitHub issue, never from imported data — no personal or pack entry has it.
        const author = typeof ruleData.author === 'string' ? ruleData.author.trim() : '';
        const authorUrl = safeCreditUrl(ruleData.authorUrl);
        const creditName = author ? this._escape(author) : '';
        const credit = author
            ? `<div class="library-card-credit">Found by ${authorUrl
                ? `<a href="${this._escapeAttr(authorUrl)}" target="_blank" rel="noopener noreferrer">${creditName}</a>`
                : creditName}</div>`
            : '';

        // ONE load control per card (roadmap #30 / UX audit fix 3). "Load" and "Load + IC" used to
        // sit side by side on every card that had a paired start — ~2 controls × every entry, which
        // is how the Library tab reached 104 controls. Which of the two a click means is now a
        // single list-level choice ("Paired start", RulesetLibraryComponent), not a per-card fork.
        // Share on Reddit likewise loses its inline button: it was always *also* in the ⋯ menu.
        const loadTitle = hasIC
            ? 'Load this ruleset — with the starting cells it was previewed from while “Paired start” is on'
            : 'Load this ruleset';
        const actions = [`<button class="button" data-action="${isPersonal ? 'load-personal' : 'load-rule'}" title="${this._escapeAttr(loadTitle)}">Load</button>`];
        if (isPersonal) {
            actions.push(`<button class="button-icon" data-action="manage-personal" title="More options">${'⋯'}</button>`);
        }

        const descText = (ruleData.description || '').trim();
        const descHtml = descText
            ? this._escape(descText)
            : (isPersonal
                ? 'No description — <span class="description-hint">Edit</span> to add one'
                : 'No description.');

        item.innerHTML = `
            <div class="library-card-thumb">${hero}${icBadge}</div>
            <div class="library-card-body">
                <div class="library-card-title">
                    <span class="name">${this._escape(ruleData.name || '')}</span>
                    ${sourceBadge}
                    ${constraintChip}
                </div>
                <div class="description${descText ? '' : ' is-empty'}">${descHtml}</div>
                ${credit}
                ${tagChips}
            </div>
            <div class="library-card-actions">${actions.join('')}</div>
        `;

        // Only the glyph fallback needs the lazy rule-viz render; a real thumbnail is already drawn.
        if (!ruleData.thumb) {
            this.observedElements.set(item, { hex: ruleData.hex });
            this.observer.observe(item);
        }
        return item;
    }

    _escape(str) {
        return String(str).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
    }

    _escapeAttr(str) {
        return String(str).replace(/"/g, '&quot;');
    }

    /**
     * Creates a list item for the Ruleset History popout.
     * @param {string} hex - The ruleset hex string.
     * @param {boolean} isCurrent - If this is the current active ruleset in the history.
     * @returns {HTMLElement} The created list item element.
     */
    createHistoryListItem(hex, isCurrent = false) {
        const item = document.createElement('div');
        item.className = 'history-item' + (isCurrent ? ' is-current' : '');
        const { name, isDerived } = this.appContext.libraryController.getDisplayName(hex);
        item.innerHTML = `
            <div class="viz-placeholder"></div>
            <div class="history-item-label">
                <div class="history-item-name${isDerived ? ' is-derived' : ''}">${name}</div>
                <div class="history-item-hex">${formatHexCode(hex)}</div>
            </div>
        `;
        if (isCurrent) {
            item.innerHTML += `<span class="tag">Current</span>`;
        }
        
        this.observedElements.set(item, { hex });
        this.observer.observe(item);
        return item;
    }

    destroy() {
        if (this.observer) {
            this.observer.disconnect();
        }
    }
}
